import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getIn } from 'formik'
import PropTypes from 'prop-types'

import MediaSkeleton from './MediaSkeleton'
import MediaThumbnail from '../../../features/components/MediaThumbnail'
import ItemsLoading from '../../../components/Loaders/ItemsLoading'
import FileDetailsPreview from '../../../components/FileDetailsPreview'

import useStyles from './styles'

const MediaFilesLibrary = ({
  values,
  type,
  mediaThumbnailFieldName,
  mediaType,
  mediaFilesSelector,
  mediaFilesIsLoadingSelector,
  mediaFilesErrorSelector,
  mediaFilesWasLoadedSelector,
  mediaFilesNextSelector,
  loadInitialMediaHandler,
  loadMoreHandler,
  clearMediaHandler,
  onMediaSelect,
  onFileRemove,
  fileNameKey,
  fileURLKey,
  uploadedMediaTypeKey
}) => {
  const classes = useStyles()

  const fileUrl = getIn(values, fileURLKey)
  const fileName = getIn(values, fileNameKey)
  const uploadedMediaType = getIn(values, uploadedMediaTypeKey)

  const mediaFiles = useSelector(mediaFilesSelector)
  const isNext = useSelector(mediaFilesNextSelector)

  const showPreview = !!fileUrl && mediaType === uploadedMediaType

  const handleRemoveFile = useCallback(() => {
    onFileRemove() && onFileRemove()
  }, [onFileRemove])

  const handleMediaClick = media => {
    onMediaSelect && onMediaSelect(media, type)
  }

  useEffect(() => {
    // clear media before initial load to avoid duplication (in case user already downloaded them)
    clearMediaHandler && clearMediaHandler()

    loadInitialMediaHandler && loadInitialMediaHandler()
  }, [clearMediaHandler, loadInitialMediaHandler])

  useEffect(
    () => () => {
      clearMediaHandler && clearMediaHandler()
    },
    [clearMediaHandler]
  )

  return showPreview ? (
    <FileDetailsPreview
      fileName={fileName}
      fileUrl={fileUrl}
      filePreviewType={type}
      removeFile={handleRemoveFile}
      allowDelete={!!fileUrl}
    />
  ) : (
    <ItemsLoading
      className={classes.library}
      SkeletonComponent={MediaSkeleton}
      isLoadingSelector={mediaFilesIsLoadingSelector}
      wasLoadedSelector={mediaFilesWasLoadedSelector}
      errorSelector={mediaFilesErrorSelector}
      itemsLength={mediaFiles.length}
      initialLoadingSkeletonsNumber={12}
      loadMore={isNext ? loadMoreHandler : null}
    >
      {mediaFiles.map(media => (
        <MediaThumbnail
          key={media.id}
          media={media}
          thumbnailUrl={media[mediaThumbnailFieldName]}
          mediaType={type}
          onMediaClick={handleMediaClick}
        />
      ))}
    </ItemsLoading>
  )
}

MediaFilesLibrary.propTypes = {
  values: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  mediaThumbnailFieldName: PropTypes.string.isRequired,
  mediaType: PropTypes.string.isRequired,
  mediaFilesSelector: PropTypes.func.isRequired,
  mediaFilesIsLoadingSelector: PropTypes.func.isRequired,
  mediaFilesErrorSelector: PropTypes.func.isRequired,
  mediaFilesWasLoadedSelector: PropTypes.func.isRequired,
  mediaFilesNextSelector: PropTypes.func.isRequired,
  loadInitialMediaHandler: PropTypes.func.isRequired,
  loadMoreHandler: PropTypes.func.isRequired,
  clearMediaHandler: PropTypes.func.isRequired,
  onMediaSelect: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  fileNameKey: PropTypes.string.isRequired,
  fileURLKey: PropTypes.string.isRequired,
  uploadedMediaTypeKey: PropTypes.string.isRequired
}

export default MediaFilesLibrary
